import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Box, Button, Typography, CircularProgress, Card, CardContent, IconButton } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import propertyService from '../../services/propertyService';
import workAnalyzerService from '../../services/workAnalyzerService';

interface PropertyInspectionUploadProps {
  propertyId: string;
  onInspectionUploaded: (newInspectionId: string) => void; // Always requires a new inspection ID
}

const PropertyInspectionUpload: React.FC<PropertyInspectionUploadProps> = ({ propertyId, onInspectionUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleClearFile = () => {
    setFile(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;

    setLoading(true);
    setLoadingMessage('Uploading inspection file...');
    try {
      // Step 1: Upload the inspection file
      const uploadResult = await propertyService.createPropertyInspectionRecord(propertyId, file);
      if (uploadResult?.id) {
        setLoadingMessage('Starting inspection analysis...');

        // Step 2: Trigger analysis - this now starts the async process but doesn't wait for completion
        await workAnalyzerService.createNewInspectionEvaluation(uploadResult.id);

        // Step 3: Trigger the callback with the new inspection ID right away
        // The parent will handle polling or status updates
        setLoadingMessage('Inspection uploaded successfully. Processing has begun and will continue in the background.');
        onInspectionUploaded(uploadResult.id); // Pass the new inspection ID to the parent
        setFile(null);
      } else {
        setLoadingMessage('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading inspection:', error);
      setLoadingMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3, width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          New Inspection
        </Typography>
        <Box component="form" onSubmit={handleSubmit} encType="multipart/form-data">
          {!file ? (
            <Button variant="contained" component="label" fullWidth>
              Select File
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdfIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="body1">{file.name}</Typography>
              </Box>
              <IconButton onClick={handleClearFile}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {file && (
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
              Analyze
            </Button>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {loadingMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PropertyInspectionUpload;
